// Импорт из других POS (iiko / r_keeper / Restik / Poster / CSV / XLSX).
// Контракт совпадает с backend/src/modules/import. Цены — в рублях, сервер
// конвертит в копейки.
import { api } from './api';

export type ImportField =
  | 'name' | 'category' | 'price' | 'cost' | 'weight' | 'unit' | 'kind' | 'section'
  | 'description' | 'sku' | 'quantity'
  | 'dishName' | 'ingredient' | 'gross' | 'net'
  | 'tableNumber' | 'tableArea' | 'seats';

export type ImportSource = 'iiko' | 'rkeeper' | 'restik' | 'poster' | 'generic';
export type SheetEntity = 'menu' | 'techcards' | 'stock' | 'tables' | 'unknown';
export type Section = 'KITCHEN' | 'BAR' | 'NONE';
export type TableArea = 'HALL' | 'TAKEAWAY';

export type MenuRow = {
  row: number;
  sheet: number;
  kind: 'dish' | 'modifier' | 'goods' | 'prep';
  category: string;
  name: string;
  priceRub: number | null;
  costRub: number | null;
  weight?: string;
  unit?: string;
  section?: Section;
  description?: string;
  sku?: string;
  quantity: number | null;
  exists: boolean;
};
export type SkippedRow = { row: number; sheet: number; name: string; reason: string };
export type TechCardRow = {
  row: number;
  sheet: number;
  dishName: string;
  ingredient: string;
  gross: number | null;
  net: number | null;
  unit?: string;
  dishExists: boolean;
  ingredientExists: boolean;
};
export type TableRow = { row: number; sheet: number; number: number; area: TableArea; seats: number | null; exists: boolean };

export type SheetInfo = {
  index: number;
  name: string;
  entity: SheetEntity;
  headerRow: number;
  headers: string[];
  mapping: Record<string, ImportField>;
  rowsTotal: number;
  sample: string[][];
};

export type AnalyzeResult = {
  file: { name: string; size: number; format: 'csv' | 'xlsx'; encoding?: string };
  source: ImportSource;
  fields: Array<{ id: ImportField; title: string }>;
  sheets: SheetInfo[];
  dishes: MenuRow[];
  modifiers: MenuRow[];
  stock: MenuRow[];
  techCards: TechCardRow[];
  tables: TableRow[];
  skipped: SkippedRow[];
  summary: {
    categories: string[];
    newCategories: string[];
    dishesNew: number;
    dishesExisting: number;
    modifiersNew: number;
    stockNew: number;
    stockExisting: number;
    techCardDishesMissing: string[];
    techCardIngredientsMissing: string[];
    tablesNew: number;
  };
  warnings: string[];
};

export type AnalyzeOverrides = {
  mapping?: Record<string, Record<string, ImportField>>;
  entities?: Record<string, SheetEntity>;
};

export async function analyzeImportFile(file: File, overrides: AnalyzeOverrides = {}): Promise<AnalyzeResult> {
  const fd = new FormData();
  fd.append('file', file);
  if (overrides.mapping) fd.append('mapping', JSON.stringify(overrides.mapping));
  if (overrides.entities) fd.append('entities', JSON.stringify(overrides.entities));
  const { data } = await api.post<AnalyzeResult>('/api/admin/import/analyze', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  });
  return data;
}

export type CommitInput = {
  dryRun?: boolean;
  updateExisting?: boolean;
  createMissingIngredients?: boolean;
  dishes?: Array<{
    category: string;
    name: string;
    priceRub: number;
    costRub?: number | null;
    weight?: string;
    section?: Section;
    description?: string;
  }>;
  modifiers?: Array<{ name: string; priceRub: number }>;
  stock?: Array<{
    name: string;
    kind?: 'GOODS' | 'PREP';
    unit?: string;
    quantity?: number | null;
    costRub?: number | null;
    sku?: string;
  }>;
  techCards?: Array<{ dishName: string; ingredient: string; net: number; unit?: string }>;
  tables?: Array<{ number: number; area?: TableArea; seats?: number | null }>;
};

export type CommitResult = {
  dryRun: boolean;
  categories: { created: number; names: string[] };
  dishes: { created: number; updated: number; skipped: number };
  modifiers: { created: number; skipped: number };
  stock: { created: number; updated: number; skipped: number };
  techCards: {
    created: number;
    updated: number;
    dishesNotFound: string[];
    ingredientsCreated: number;
    ingredientsNotFound: string[];
  };
  tables: { created: number; skipped: number };
};

// Большой импорт (тысячи блюд + тех-карты) пишется одной транзакцией дольше
// дефолтного таймаута axios — задаём свой.
export async function commitImport(input: CommitInput): Promise<CommitResult> {
  const { data } = await api.post<CommitResult>('/api/admin/import/commit', input, { timeout: 180_000 });
  return data;
}
